import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { ConfirmationModalRef, ConfirmationModalService } from '@tablesready/common-ui';
import {
  from,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { UserLocationRole } from '@shared/user/enums';
import { AuthUser } from '@shared/signup/models';
import { CapacitorEventBird } from 'capacitor-event-bird';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { SubscriptionState } from '@shared/recurly/enums';

export enum StoreCountries {
  USA = 'USA',
  KAZ = 'KAZ',
  NIL = 'NIL',
}

export enum BillingVisibility {
  LINKS_ONLY_ON_MOBILE = 'LINKS_ONLY_ON_MOBILE',
  STARTER_PLAN_OUTSIDE_US = 'STARTER_PLAN_OUTSIDE_US',
  BUSINESS_PLAN_OUTSIDE_US = 'BUSINESS_PLAN_OUTSIDE_US',
  FULL = 'FULL',
  HIDDEN_ON_MOBILE = 'HIDDEN_ON_MOBILE',
}

@Injectable({ providedIn: 'root' })
export class FrontendPaidFeatureService {
  storeCountry?: StoreCountries;

  private user?: AuthUser;

  constructor(
    private readonly router: Router,
    private readonly restrictionsService: PricePlansRestrictionsService,
    private readonly confirmationModalService: ConfirmationModalService,
  ) {
    from(CapacitorEventBird.getAppStoreCountry())
      .pipe(take(1))
      .subscribe((country) => {
        this.storeCountry = country.country as StoreCountries;
        //this.storeCountry = StoreCountries.KAZ;
        //this.storeCountry = StoreCountries.USA;
      });
  }

  setUser(user: AuthUser): void {
    this.user = user;
  }

  get billingsVisibility(): BillingVisibility {
    if (Capacitor.isNativePlatform() && this.storeCountry === 'USA') {
      return BillingVisibility.LINKS_ONLY_ON_MOBILE;
    }

    if (this.user && this.user?.account && this.user.account.subscriptionType === SubscriptionTypes.offline) {
      return BillingVisibility.LINKS_ONLY_ON_MOBILE;
    }

    if (
      this.user?.account &&
      this.user?.location &&
      Capacitor.isNativePlatform() &&
      this.restrictionsService.availableForBusinessPlan(this.user.account.subscriptionType, this.user.location.recurly)
    ) {
      return BillingVisibility.BUSINESS_PLAN_OUTSIDE_US;
    }

    if (
      this.user?.account &&
      this.user?.location &&
      Capacitor.isNativePlatform() &&
      this.restrictionsService.availableForStarterPlan(this.user.account.subscriptionType, this.user.location.recurly)
    ) {
      return BillingVisibility.STARTER_PLAN_OUTSIDE_US;
    }

    if (Capacitor.isNativePlatform() && this.storeCountry !== 'USA') {
      return BillingVisibility.HIDDEN_ON_MOBILE;
    }

    return BillingVisibility.FULL;
  }

  getBillingText(textForWeb: string, textForApple: string | null): string | null {
    if (this.billingsVisibility === BillingVisibility.FULL) {
      return textForWeb;
    }

    if (this.billingsVisibility === BillingVisibility.LINKS_ONLY_ON_MOBILE) {
      return textForApple;
    }

    return null;
  }

  freePlanLocationModalDisplay(user: AuthUser, starterPlanFeature = false): Observable<ConfirmationModalRef<boolean>> {
    if (Capacitor.isNativePlatform() && this.billingsVisibility === BillingVisibility.HIDDEN_ON_MOBILE) {
      return of({
        isConfirmed: false,
        close: () => {},
        dismiss: () => {},
      });
    }

    const title = starterPlanFeature ? 'Starter Plan Feature' : 'Business Plan Feature';
    const isUser = user.locationUser?.role === UserLocationRole.user;
    const subscriptionPaused = user.location?.recurly.subscription_state === SubscriptionState.paused;
    const options = isUser
      ? {
        title,
        content: 'Ask your location manager to upgrade your plan today',
        confirmBtnText: 'Confirm',
        backdropDismiss: false,
      } : {
        title,
        content: subscriptionPaused ? 'Reactivate your subscription to unlock this feature.' : 'Upgrade your plan to unlock this feature today!',
        confirmBtnText: subscriptionPaused ? 'Reactivate subscription' : 'Upgrade your plan',
        closeBtnText: 'Cancel',
        backdropDismiss: false,
      };

    return this.confirmationModalService.confirm(options)
      .pipe(
        tap(({ isConfirmed }) => {
          if (isConfirmed && !isUser) {
            void this.router.navigateByUrl('/settings/location/billing');
          }
        }),
      );
  }
}
